"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { isInterviewSessionPath } from "@/lib/paths";
import { useMemberNavVisibility } from "@/hooks/useMemberNavVisibility";
import NavBadge from "./NavBadge";
import {
  getMemberNavBadge,
  getVisibleMemberNavItems,
  isMemberNavItemActive,
  type MemberNavItem,
  type NavBadgeSpec,
} from "./memberNavItems";

// モバイル時のグローバルナビ。ヘッダーの直下に貼り付ける。
// PC では主要5項目を縦型サイドメニュー（AppSidebar）が、求人・応募状況を
// ヘッダー右の副ナビが担うためここでは描画しない（`md:hidden`）。
// モバイルにはヘッダー副ナビを置く幅がないため、求人・応募状況もこの帯に含める。
//
// 項目は memberNavItems.tsx が唯一の定義。並び順・ラベル・アイコンをサイドメニューと揃えるため、
// ここで項目を足したり順番を変えたりしない。
export default function BottomNav() {
  const pathname = usePathname();
  const { user, loading } = useAuth();
  // 表示条件（求人区分・大学連携）は1つのフックでまとめて解決する
  const navVisibility = useMemberNavVisibility();

  // 面接セッション（本番）中は没入表示のためナビを隠す
  if (isInterviewSessionPath(pathname)) return null;

  if (pathname === "/practice/select" && (loading || !user)) {
    return null;
  }

  const items = getVisibleMemberNavItems("mobile", navVisibility);

  return (
    <nav
      className="sticky top-16 z-40 bg-surface py-2 md:hidden"
      aria-label="モバイルナビゲーション"
    >
      {/* SVG gradient definition（アクティブアイコン用、画面には描画しない） */}
      <svg
        width="0"
        height="0"
        className="absolute pointer-events-none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="nav-active-gradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#e95962" />
            <stop offset="100%" stopColor="#f67a50" />
          </linearGradient>
        </defs>
      </svg>

      {/* 機能フラグ・求人区分次第で最大7項目になり 375px 幅に収まらないため、
          溢れた分は横スクロールで到達できるようにする。
          内側を w-max にするのは、justify-around のまま溢れさせると余白が負になり
          space-around が center にフォールバックして先頭項目が左に切れる（スクロールでも届かない）ため。
          min-w-full で、項目が収まるときは従来どおり均等配置を保つ。 */}
      <div className="overflow-x-auto max-w-7xl mx-auto">
        <div className="flex items-center justify-around h-14 w-max min-w-full">
          {items.map((item) => (
            <NavItem
              key={item.href}
              item={item}
              active={isMemberNavItemActive(item, pathname)}
              badge={getMemberNavBadge(item, navVisibility)}
            />
          ))}
        </div>
      </div>
    </nav>
  );
}

function NavItem({
  item,
  active,
  badge,
}: {
  item: MemberNavItem;
  active: boolean;
  badge?: NavBadgeSpec;
}) {
  // アクティブ時はグラデーション、非アクティブ時は gray-300 でストロークを描画する。
  // currentColor を介すとテキスト色と icon 色を別々に制御できないため、明示的に stroke を渡す。
  const stroke = active ? "url(#nav-active-gradient)" : "#d1d5db"; // gray-300

  return (
    <Link
      href={item.href}
      className={`relative flex flex-col items-center gap-0.5 px-2 py-2 min-w-[56px] ${
        active ? "text-primary font-bold" : "text-gray-300"
      }`}
      {...(active ? { "aria-current": "page" as const } : {})}
    >
      {badge && (
        <NavBadge label={badge.label} tone={badge.tone} className="top-0 right-0.5 px-1 py-px" />
      )}
      {item.renderIcon?.({ stroke, className: "w-6 h-6" })}
      <span className="text-[10px] tracking-tight whitespace-nowrap">
        {item.shortLabel}
      </span>
    </Link>
  );
}
